import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, Input, Card } from '@/components/ui'
import { api } from '@/services/api'

interface ForgotPasswordFormData {
  email: string
}

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>()

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)

    try {
      await api.requestPasswordReset(data.email)
    } catch {
      // Silently handle errors - we always show success to prevent email enumeration
    } finally {
      setIsLoading(false)
      setIsSubmitted(true)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-medium">
            Beacon
          </Link>
          <p className="text-secondary-text mt-2">Reset your password</p>
        </div>

        <Card>
          {isSubmitted ? (
            <div className="text-center py-4">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-medium mb-2">Check your email</h2>
              <p className="text-secondary-text text-sm mb-6">
                If an account exists with this email, you'll receive a password reset link shortly.
              </p>
              <Link
                to="/login"
                className="text-sm text-primary-text hover:underline"
              >
                Back to login
              </Link>
            </div>
          ) : (
            <>
              <p className="text-secondary-text text-sm mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={errors.email?.message}
                />

                <Button type="submit" loading={isLoading} className="w-full">
                  Send Reset Link
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-secondary-text hover:text-primary-text"
                >
                  Back to login
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
